import React, { useState, useEffect, useContext, useRef } from 'react';
import { Row, Col, Button, DropdownButton, Dropdown, ToggleButton, ToggleButtonGroup, Form } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import chestRare from './img/purchase-chest-rare.png';
import chestEpic from './img/purchase-chest-epic.png';
import chestLegend from './img/purchase-chest-legend.png';
import { Web3Context } from '../../web3/Web3Context';
import useDebounce from '../../helpers/useDebounce';
import Web3 from 'web3';
import { customConfirm, ConnectModal } from '../../components/Connect';
import { fromWei, toHex } from 'web3-utils';
import { PlayerIDModal, PendingModal, SuccessModal, Declined, NetworkModal, ErrorModal } from './Modal';
import './Purchase.scss';

const cratesData = {
    "rare": {
        lot: 1,
        title: "Rare Chest",
        description: "A Rare Chest contains a 2-star collectible card, a Satchel of Gems (6,000) and 600 VIP Points!",
        gem: "6,000",
        vip: 600,
        limit: 60,
        price: "US$20",
        img: chestRare,
        icon: require('./img/rare-icon.png')
    },
    "epic": {
        lot: 2,
        title: "Epic Chest",
        description: "An Epic Chest contains a 3-star Collectible Card, a Big Sack of Gems (15,120) and 1,500 VIP Points!",
        gem: "15,120",
        vip: 1500,
        limit: 35,
        price: "US$50",
        img: chestEpic,
        icon: require('./img/epic-icon.png')
    },
    "legend": {
        lot: 3,
        title: "Legendary Chest",
        description: "A Legendary Chest contains a 4-star Collectible Card, a Cartful of Gems (32,400) and 3,000 VIP Points!",
        gem: "32,400",
        vip: 3000,
        limit: 5,
        price: "US$100",
        img: chestLegend,
        icon: require('./img/legend-icon.png')
    }
}

function Purchase(props) {
    const [t] = useTranslation();
    const [selected, setSelected] = useState('legend');
    const [purchaseAmount, setPurchaseAmount] = useState(5);
    // const [userWallet, setUserWallet] = useState(null);
    const [customPurchaseAmount, setCustomPurchaseAmount] = useState(5);
    const [paymentOption, setPaymentOption] = useState("DAI");
    const {connect, getWeb3Manager, getNetworkType} = useContext(Web3Context);
    const [numAvailable, setNumAvailable] = useState(['-', '-', '-']);
    const [subTotal, setSubTotal] = useState('-');
    const [platform, setPlatform] = useState('ios');
    const playerIdRef = useRef(null);
    const [max, setMax] = useState(5);

    const [disable1, setDisable1] = useState(false);
    const [disable5, setDisable5] = useState(false);
    const [disable10, setDisable10] = useState(false);
    const [disable20, setDisable20] = useState(false);
    const [disableBuy, setDisableBuy] = useState(true);

    const dbSelected = useDebounce(selected, 500);
    const dbPurchaseAmount = useDebounce(purchaseAmount, 500);
    const dbCustomAmount = useDebounce(customPurchaseAmount, 500);

    const [showPending, setShowPending] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showWarn, setShowWarn] = useState(false);
    const [showDeclined, setShowDeclined] = useState(false);
    const [showNetwork, setShowNetwork] = useState(false);
    const [showError, setShowError] = useState(false);

    // get remaining supply
    useEffect(() => {
        const fetchData = async () => {
            const web3Manager = await getWeb3Manager();
            const contract = await web3Manager.getContract("CDHSale")

            let supply = await Promise.all([
                contract.methods._lots(1).call(),
                contract.methods._lots(2).call(),
                contract.methods._lots(3).call()
            ]).catch(e => console.error(e))
            
            if(supply) {
                setNumAvailable(supply.map(x => x.numAvailable))
            }
        }

        fetchData();
    }, [])

    useEffect(() => {
        if(numAvailable[0] !== '-') {
            disableQuantitySelection(selected)
            let idx = cratesData[selected].lot - 1
            setMax(parseInt(numAvailable[idx]))
        }
    }, [numAvailable, dbSelected])

    useEffect(() => {
        setSubTotal('-');
    }, [paymentOption, selected, purchaseAmount, customPurchaseAmount])

    useEffect(() => {
        const fetchData = async () => {
            let {totalPrice, totalDiscounts} = await getTotalPrice();

            setSubTotal(fromWei(totalPrice.toString()))
            setDisableBuy(false)
        }

        fetchData()
    }, [paymentOption, dbSelected, dbPurchaseAmount, dbCustomAmount])

    useEffect(() => {
        disableQuantitySelection(dbSelected)
    }, [dbSelected])

    const disableQuantitySelection = (rarity) => {
        let idx = parseInt(cratesData[rarity].lot) - 1;
        setDisable20(numAvailable[idx] < 20);
        setDisable10(numAvailable[idx] < 10);
        setDisable5(numAvailable[idx] < 5);
        setDisable1(numAvailable[idx] < 1);

        setDisableBuy(numAvailable[idx] === 0);

        let quantity = purchaseAmount
        if(isNaN(purchaseAmount) && purchaseAmount === "custom") {
            quantity = parseInt(customPurchaseAmount)
        }

        if(numAvailable[idx] < quantity) {
            setDisableBuy(true);
        }
    }

    const getTotalPrice = async () => {
        let tokenAddress = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
        let walletAddress = "0x0000000000000000000000000000000000000000"
        let quantity = purchaseAmount
        if(isNaN(purchaseAmount) && purchaseAmount === "custom") {
            quantity = parseInt(customPurchaseAmount)
        }

        let lotId = cratesData[selected].lot
        const web3Manager = await getWeb3Manager();
        const contract = await web3Manager.getContract("CDHSale");

        try {
            if(paymentOption !== "ETH") {
                tokenAddress = (await web3Manager.getContract("DAI")).options.address
            }
        } catch(e) {
            return Promise.reject(e)
        }

        // console.log(walletAddress, lotId, quantity, tokenAddress)
        let res = await contract.methods.getPrice(walletAddress, lotId, quantity, tokenAddress)
            .call()
            .catch(e => console.error(e))

        if(!res) {
            console.error('Failed to call getPrice from sales contract')
            return {
                totalPrice: 0,
                totalDiscounts: 0
            }
        }

        return res;
    }

    const purchaseWithWallet = async () => {
        if(!playerIdRef.current.value) {
            setShowWarn(true)
            return;
        }

        let wallet = await customConfirm(ConnectModal)
        if(!wallet) {
            return;
        }
        let connected = await connect({connector: wallet})
        if(!connected) {
            return;
        }
        let {selectedAccount, web3} = connected
        let walletAddress = selectedAccount
        // setUserWallet(selectedAccount)

        const networkId = await getNetworkType(wallet)
        if(networkId !== process.env.REACT_APP_ETHEREUM_NETWORK) {
            setShowNetwork(true);
            return;
        }

        let tokenAddress = "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
        let quantity = purchaseAmount

        if(isNaN(purchaseAmount) && purchaseAmount === "custom") {
            quantity = parseInt(customPurchaseAmount)
        }

        const web3Manager = await getWeb3Manager();
        const contract = await web3Manager.getContract("CDHSale", web3)
        let lotId = cratesData[selected].lot

        try {
            if(paymentOption !== "ETH") {
                tokenAddress = (await web3Manager.getContract("DAI")).options.address
            }
        } catch(e) {
            return Promise.reject(e)
        }

        // console.log(walletAddress, lotId, quantity, tokenAddress)
        let res = await contract.methods.getPrice(walletAddress, lotId, quantity, tokenAddress)
            .call()
            .catch(e => console.error(e))

        setShowPending(true)
        // Approve contract to spend token
        if(paymentOption !== "ETH") {
            const tokenContract = await web3Manager.getContract("DAI", web3);
            const approve = await tokenContract.methods.approve(contract.options.address, res.totalPrice).send({from: selectedAccount})
                .catch(e => console.error(e));

            if(!approve) {
                setShowPending(false);
                setShowDeclined(true);
                return;
            }
        }

        const purchaseMethod = await contract.methods.purchaseFor(
            selectedAccount,
            lotId, 
            quantity, 
            tokenAddress,
            res.totalPrice,
            res.minConversionRate,
            `${platform}-${playerIdRef.current.value}`
        )

        let gasAmount = await purchaseMethod.estimateGas({
            from: selectedAccount,
            gas: 8000000
        }).catch(e => console.error(e))
        console.log('estimated gas amount', gasAmount);

        // gasAmount = isNaN(gasAmount) ? 5000000 : Math.max(gasAmount, 5000000)
        gasAmount = 8000000;
        
        let txOptions = {
            from: selectedAccount,
            gas: toHex(gasAmount)
        };
        if(paymentOption === "ETH") {
            txOptions.value = res.totalPrice
        }
        
        let tx = await purchaseMethod
            .send(txOptions)
            .catch(e => {
                setShowPending(false);
                if(e && e.code === 4001) {
                    // user declined
                    setShowDeclined(true);
                } else {
                    setShowError(true)
                }
            });

        setShowPending(false);

        if(tx) {
            setShowSuccess(true)
            let newNumAvail = numAvailable.slice()
            newNumAvail[cratesData[selected].lot - 1] = parseInt(newNumAvail[cratesData[selected].lot - 1]) - quantity
            setNumAvailable(newNumAvail)
        }
    }

    const handleChestSelect = (chest) => {
        setSelected(chest)
    }

    const handlePlatformSelect = (value) => {
        setPlatform(value)
    }

    const handlePurchaseAmountChange = (value) => {
        setPurchaseAmount(value)
    }

    const customBoxChanged = (e) => {
        setPurchaseAmount('custom')
        setCustomPurchaseAmount(e.target.value)
    }

    const customBoxBlur = (e) => {
        let val = parseInt(e.target.value.trim());

        if (isNaN(val) || val < 0) {
            setCustomPurchaseAmount(0)
        }

        let idx = parseInt(cratesData[selected].lot) - 1;
        if(numAvailable[idx] < val) {
            setCustomPurchaseAmount(parseInt(numAvailable[idx]))
        }
    }

    const handleCustomBoxSlide = (delta) => {
        let val = parseInt(customPurchaseAmount)
        setCustomPurchaseAmount(Math.max(val + delta, 0))
    }


    return <section className="purchase" id="buy-now">
        <h2>{t("Buy Now")}</h2>
        <div className="purchase-wrapper">
            <div className="left">
                <h3>{t(cratesData[selected].title)} {t(cratesData[selected].price)}</h3>
                <p>{t(cratesData[selected].description)}</p>
                <p>{t("*Note: These Collectible Cards are for trading only and can't be used in the game.")}</p>
                <div className="chest-info">
                    <div className="left selectors">
                        <ToggleButtonGroup type="radio" name="rarity" defaultValue={'legend'} onChange={handleChestSelect}>
                            {Object.keys(cratesData).map((chest, i) => {
                                return <ToggleButton variant="no-var" value={chest} key={`toggle_${i}`}>
                                    <div className="chest-icon">
                                        <img src={cratesData[chest].icon} />
                                        <span className="remaining">{numAvailable[i]}/{cratesData[chest].limit}</span>
                                    </div>
                                </ToggleButton>
                            })}
                        </ToggleButtonGroup>
                    </div>
                    <div className="right">
                        <img src={cratesData[selected].img} />
                    </div>
                </div>
            </div>
            <div className="right buy-panel">
                {/* <Form.Group className="user-wallet">
                    <Form.Label className="wallet-label"><p>{t('Wallet Address')}</p></Form.Label>
                    <Form.Control plaintext={true} readOnly={true} defaultValue={userWallet} />
                </Form.Group> */}
                <div className="game-platform">
                    <p>{t('Platform')}</p>
                    <ToggleButtonGroup type="radio" name="platform" defaultValue="ios" onChange={handlePlatformSelect}>
                        <ToggleButton variant="cdh-outline" value="ios">IOS</ToggleButton>
                        <ToggleButton variant="cdh-outline" value="android">Android</ToggleButton>
                    </ToggleButtonGroup>
                </div>
                <Form.Group className="player-id-form">
                    <Form.Label className="player-id-label"><p>{t('Player ID')}</p></Form.Label>
                    <Form.Control ref={playerIdRef} name="nickname" className="profile-info-input" type="text" />
                    <p>{t('Your Player ID is case sensitive. Please ensure it is correct.')}</p>
                </Form.Group>
                <div className="chest-selection">
                    <ToggleButtonGroup name="quant-selection" value={purchaseAmount} onChange={handlePurchaseAmountChange}>
                        <ToggleButton variant="f1-add-tires" value={1} disabled={disable1}>1</ToggleButton>
                        <ToggleButton variant="f1-add-tires" value={5} disabled={disable5}>5</ToggleButton>
                        <ToggleButton variant="f1-add-tires" value={10} disabled={disable10}>10</ToggleButton>
                        <ToggleButton variant="f1-add-tires" value={20} disabled={disable20}>20</ToggleButton>
                        <ToggleButton variant="f1-add-tires custom" value={'custom'}>
                            <FontAwesomeIcon icon={['fas', 'minus-circle']} onClick={() => handleCustomBoxSlide(-1)} />
                            <Form.Control className="custom-input" type="number" min={1} max={max} value={customPurchaseAmount} onChange={customBoxChanged} onBlur={customBoxBlur} />
                            <FontAwesomeIcon icon={['fas', 'plus-circle']} onClick={() => handleCustomBoxSlide(1)} />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <div className="sub-total">
                        <div className="st-row">
                            <span>{t('Total Price')}</span>
                            <span className="f1-red">{paymentOption} <span className="exact-amount">{!isNaN(subTotal) && parseFloat(subTotal).toFixed(4)}&nbsp;</span></span>
                        </div>
                    </div>
                </div>
                <div className="purchase-method">
                    <p>{t('Payment Method')}</p>
                    <DropdownButton variant="cdh-dropdown" title={paymentOption} onSelect={setPaymentOption}>
                        <Dropdown.Item key="eth" eventKey={"ETH"}>ETH</Dropdown.Item>
                        <Dropdown.Item key="dai" eventKey={"DAI"}>DAI</Dropdown.Item>
                    </DropdownButton>
                </div>
                <Button variant="cdh-primary" onClick={purchaseWithWallet} disabled={disableBuy} block>{t('Buy')}</Button>
            </div>
        </div>
        <PendingModal show={showPending} setShow={setShowPending} imgSrc={cratesData[selected].img} item={cratesData[selected].title} />
        <SuccessModal show={showSuccess} setShow={setShowSuccess} imgSrc={cratesData[selected].img} item={cratesData[selected].title} />
        <PlayerIDModal show={showWarn} setShow={setShowWarn} />
        <Declined show={showDeclined} setShow={setShowDeclined} />
        <NetworkModal show={showNetwork} setShow={setShowNetwork} />
        <ErrorModal show={showError} setShow={setShowError} />
    </section>
}

export { Purchase };